import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, Trash2, RefreshCw, Loader2, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 16).replace('T', ' ');
  } catch {
    return iso;
  }
}

export default function AdminWaitlistViewer({ token }) {
  const [users, setUsers] = useState([]);
  const [sources, setSources] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'landing_page' | 'about_page'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/waitlist/list?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data.users || []);
        setSources(data.sources || {});
        setTotal(data.total || 0);
      } else {
        toast.error(data.detail || 'Failed to load waitlist');
      }
    } catch {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete this email from waitlist?\n\n${email}`)) return;
    setDeletingEmail(email);
    try {
      const res = await fetch(`${API_URL}/api/admin/waitlist/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Removed');
        setUsers((prev) => prev.filter((u) => u.email !== email));
        // Refresh counts in background (sources reflect full DB, not just the latest 20)
        load();
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingEmail(null);
    }
  };

  const visibleUsers = filter === 'all' ? users : users.filter((u) => u.source === filter);

  const handleCopyEmails = async () => {
    const emails = visibleUsers.map((u) => u.email).filter(Boolean);
    if (emails.length === 0) {
      toast.error('No emails to copy');
      return;
    }
    const text = emails.join(', ');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      toast.success('Emails copied to clipboard');
    } catch {
      toast.error('Failed to copy emails');
    }
  };

  return (
    <Card className="p-6 border-border/40 bg-card" data-testid="admin-waitlist-viewer-card">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-semibold text-foreground">Latest Waitlist Signups</h2>
            <p className="text-sm text-muted-foreground">Most recent 20 entries</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleCopyEmails}
            variant="outline"
            size="sm"
            disabled={loading || visibleUsers.length === 0}
            className="border-border/50"
            data-testid="waitlist-copy-emails-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy Emails
          </Button>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            disabled={loading}
            className="border-border/50"
            data-testid="waitlist-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Signup sources → clickable filter pills */}
      <div
        className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3 text-sm"
        data-testid="waitlist-sources-breakdown"
      >
        <span className="text-muted-foreground text-xs uppercase tracking-wider mr-1">
          Signup sources
        </span>

        {/* All pill */}
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs transition-colors ${
            filter === 'all'
              ? 'bg-white/15 border-white/30 text-white'
              : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
          }`}
          data-testid="source-filter-all"
        >
          <span className="font-medium">All</span>
          <span className="font-semibold">{total}</span>
        </button>

        {Object.entries(sources)
          .sort((a, b) => b[1] - a[1])
          .map(([src, count]) => {
            const active = filter === src;
            const palette =
              src === 'about_page'
                ? active
                  ? 'bg-purple-500/25 border-purple-500/50 text-purple-100'
                  : 'bg-purple-500/10 border-purple-500/20 text-purple-300 hover:bg-purple-500/15'
                : src === 'landing_page'
                ? active
                  ? 'bg-blue-500/25 border-blue-500/50 text-blue-100'
                  : 'bg-blue-500/10 border-blue-500/20 text-blue-300 hover:bg-blue-500/15'
                : active
                ? 'bg-white/15 border-white/30 text-white'
                : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10';
            return (
              <button
                key={src}
                type="button"
                onClick={() => setFilter(src)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs transition-colors ${palette}`}
                data-testid={`source-filter-${src}`}
              >
                <span className="font-medium">{src}</span>
                <span className="font-semibold">{count}</span>
              </button>
            );
          })}

        {filter !== 'all' && (
          <span className="text-xs text-muted-foreground ml-1" data-testid="active-filter-indicator">
            · filtered
          </span>
        )}
      </div>

      {(() => {
        if (loading && users.length === 0) {
          return (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          );
        }
        if (users.length === 0) {
          return (
            <div className="py-8 text-center text-muted-foreground text-sm">
              No waitlist signups yet.
            </div>
          );
        }
        if (visibleUsers.length === 0) {
          return (
            <div className="py-8 text-center text-muted-foreground text-sm" data-testid="waitlist-filter-empty">
              No signups from <span className="text-foreground font-medium">{filter}</span> in the latest {users.length} entries.
            </div>
          );
        }
        return (
          <div className="overflow-x-auto -mx-6 sm:mx-0" data-testid="waitlist-table-wrap">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/30">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Email</th>
                  <th className="text-left font-medium px-3 py-2">Joined</th>
                  <th className="text-left font-medium px-3 py-2">Source</th>
                <th className="text-left font-medium px-3 py-2">Consent</th>
                <th className="text-left font-medium px-3 py-2">Consent at</th>
                <th className="text-right font-medium px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((u) => (
                <tr
                  key={u.email}
                  className="border-b border-border/10 hover:bg-white/[0.02] transition-colors"
                  data-testid={`waitlist-row-${u.email}`}
                >
                  <td className="px-3 py-2 font-medium text-foreground break-all">{u.email}</td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {formatDate(u.date_joined)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                      u.source === 'about_page'
                        ? 'bg-purple-500/10 text-purple-400'
                        : 'bg-blue-500/10 text-blue-400'
                    }`}>
                      {u.source || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {u.consent_given ? (
                      <span className="text-green-400 text-xs font-medium">Yes</span>
                    ) : (
                      <span className="text-gray-500 text-xs">No</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap text-xs">
                    {formatDate(u.consent_timestamp)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      onClick={() => handleDelete(u.email)}
                      disabled={deletingEmail === u.email}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`waitlist-delete-${u.email}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {deletingEmail === u.email ? 'Deleting…' : 'Delete'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        );
      })()}
    </Card>
  );
}
